import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { Security } from '../../proto/generated/security'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { getCandles } from '../../services/apis/candles'
import { getSecurityKey } from '../../util/security'
import { SeasonalYearSeries, buildSeasonalsData } from '../../util/seasonals'
import useOverviewPanelNavigation from '../../hooks/useOverviewPanelNavigation'
import SeasonalsChart from './SeasonalsChart'
import OverviewSectionHeader from './OverviewSectionHeader'
import { OverviewSection, OVERVIEW_SECTION_IDS, OVERVIEW_SECTION_ORDER } from './OverviewSectionNav'

type SeasonalsSectionProps = {
    security: Security | undefined
}

/** ~3.5 years of trading days, enough to cover the current year plus the previous two full years. */
const SEASONALS_BACKWARD_CANDLE_COUNT = 900

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(0, 2, 2)
    },
    legend: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'center',
        marginTop: theme.spacing(1)
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    legendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0
    },
    legendLabel: {
        fontSize: '12px',
        color: theme.palette.text.secondary
    },
    moreButton: {
        marginTop: theme.spacing(1),
        textTransform: 'none',
        fontSize: '12px',
        fontWeight: 500,
        justifyContent: 'space-between'
    }
}))

const SeasonalsSection = ({ security }: SeasonalsSectionProps) => {
    const { classes } = useStyles()
    const [series, setSeries] = useState<SeasonalYearSeries[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const openSeasonalsPanel = useOverviewPanelNavigation('seasonals')

    const handleOpenSeasonals = useCallback(() => {
        openSeasonalsPanel(security)
    }, [openSeasonalsPanel, security])

    const securityKey = security ? getSecurityKey(security.exchange, security.code) : null

    // Keyed on exchange+code so a new `security` object identity alone does not refetch.
    const securityRef = useRef(security)
    securityRef.current = security

    useEffect(() => {
        const current = securityRef.current
        if (!current || !securityKey) {
            setSeries([])
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)
        // The previous chart stays on screen until the new series lands, like the chart and
        // range sections do, instead of collapsing the section.

        ;(async () => {
            try {
                const response = await getCandles({
                    body: {
                        security: { exchange: current.exchange, code: current.code },
                        timeBucket: { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 },
                        referenceTime: Date.now(),
                        forwardCandleCount: 0,
                        backwardCandleCount: SEASONALS_BACKWARD_CANDLE_COUNT,
                        dontGiveExtraCandles: false,
                        dontApplyCorporateActionAdjustments: false
                    }
                })
                if (cancelled) return
                setSeries(buildSeasonalsData(response.backwardCandles, { priceMultiplier: current.priceMultiplier ?? 100 }))
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching seasonals data', error)
                    setSeries([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [securityKey])

    if (!security) return null

    if (series.length === 0) {
        if (!loading) return null
        return (
            <OverviewSection
                id={OVERVIEW_SECTION_IDS.seasonals}
                label="Seasonals"
                order={OVERVIEW_SECTION_ORDER.seasonals}
            >
            <Box boxShadow={0}>
                <OverviewSectionHeader title="Seasonals" />
                <Box className={classes.container}>
                    <Skeleton variant="rounded" height={200} />
                </Box>
            </Box>
            </OverviewSection>
        )
    }

    return (
        <OverviewSection
            id={OVERVIEW_SECTION_IDS.seasonals}
            label="Seasonals"
            order={OVERVIEW_SECTION_ORDER.seasonals}
        >
        <Box boxShadow={0} sx={{ opacity: loading ? 0.72 : 1, transition: 'opacity 0.2s ease' }}>
            <OverviewSectionHeader title="Seasonals" />
            <Box className={classes.container}>
                <SeasonalsChart series={series} />
                <Box className={classes.legend}>
                    {series.map((yearSeries) => (
                        <Box key={yearSeries.year} className={classes.legendItem}>
                            <Box className={classes.legendDot} sx={{ backgroundColor: yearSeries.color }} />
                            <Typography className={classes.legendLabel}>{yearSeries.year}</Typography>
                        </Box>
                    ))}
                </Box>
                <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    size="small"
                    className={classes.moreButton}
                    endIcon={<ChevronRightIcon fontSize="small" />}
                    onClick={handleOpenSeasonals}
                >
                    More seasonals
                </Button>
            </Box>
        </Box>
        </OverviewSection>
    )
}

export default memo(SeasonalsSection)
